/* HTML versions of the guest houses' paper application forms, each laid out
   after its printed original, with a booking's details written onto the
   dotted lines. Every value is escaped — it is free text an applicant typed.
   Printed through the browser (see utils/printHtml.js) because the browser
   shapes Devanagari properly; "Save as PDF" in the print dialog gives the
   PDF. */
import { fmtDateTime } from "../adminHelpers";

export function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/* A dotted line with the value written on it; empty stays a bare line. */
const line = (value, cls = "") =>
  `<span class="line ${cls}">${escapeHtml(value)}</span>`;

/* The allotted rooms in a bordered cell at the top left. A long list drops
   a size so it stays on one line and the form stays on one page. */
const roomNoBox = (numbers) =>
  `<span class="val${String(numbers).length > 32 ? " long" : ""}">${escapeHtml(numbers)}</span>`;

const box = (checked) => `<span class="box">${checked ? "&#10003;" : ""}</span>`;

const STYLE = `
  @page { size: A4; margin: 11mm 13mm 16mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; }
  body {
    font-family: "Times New Roman", Times, "Noto Sans Devanagari", serif;
    font-size: 11.5px; line-height: 1.35;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .hi { font-family: "Noto Sans Devanagari", "Nirmala UI", Mangal, sans-serif; }
  .page { width: 100%; position: relative; }
  .ref { position: absolute; top: -6mm; left: 0; font-size: 8.5px; color: #555; }
  .center { text-align: center; }
  .right { text-align: right; }
  .bold { font-weight: 700; }
  .small { font-size: 10px; }
  .head1 { font-size: 15px; font-weight: 700; text-align: center; margin: 0; }
  .title { text-align: center; margin: 8px 0 10px; }
  .title .hi { font-size: 15px; font-weight: 700; display: block; }
  .title .en { font-size: 12px; font-weight: 700; }
  .line {
    display: inline-block; min-width: 60px; flex: 1;
    border-bottom: 1px dotted #000; min-height: 1.3em;
    padding: 0 4px; font-family: Arial, Helvetica, sans-serif; font-weight: 600;
  }
  .line.short { flex: 0 0 90px; }
  .items { list-style: none; margin: 0; padding: 0; }
  .item { display: grid; grid-template-columns: 18px 44% 1fr; column-gap: 6px; margin: 4px 0; align-items: start; }
  .item .lines { display: flex; flex-direction: column; gap: 5px; }
  .item .lines > div { display: flex; gap: 4px; align-items: flex-end; }
  table.stay { border-collapse: collapse; margin: 6px 0 6px auto; width: 58%; }
  table.stay td, table.stay th { border: 1px solid #000; padding: 3px 6px; font-weight: 400; height: 22px; }
  table.stay td.val { font-family: Arial, Helvetica, sans-serif; font-weight: 600; text-align: center; }
  .topline { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; }
  .topline .line { flex: 0 0 auto; min-width: 110px; }
  .topline .room-no { font-size: 15px; font-weight: 700; line-height: 1.2; display: flex; align-items: center; gap: 6px; max-width: 72%; }
  .topline .room-no > span:first-child { white-space: nowrap; }
  .topline .room-no .val {
    font-family: Arial, Helvetica, sans-serif; border: 1.5px solid #000;
    padding: 1px 8px; min-width: 90px; min-height: 1.4em;
  }
  .topline .room-no .val.long { font-size: 12px; }
  .cols { display: grid; gap: 14px; }
  .sig { display: flex; flex-direction: column; gap: 6px; }
  .sig .row { display: flex; gap: 4px; align-items: flex-end; }
  .notes { font-size: 10px; margin: 0; padding-left: 16px; }
  .notes li { margin-bottom: 2px; }
  hr { border: 0; border-top: 1px solid #000; margin: 10px 0 6px; }
  .box {
    display: inline-block; width: 15px; height: 15px; border: 1.2px solid #000;
    text-align: center; line-height: 13px; font-size: 13px; font-weight: 700; vertical-align: middle;
  }
  .more { font-size: 9.5px; font-style: italic; }
  .footer {
    position: fixed; bottom: -10mm; left: 0; right: 0;
    font-size: 8.5px; color: #555; text-align: right;
  }
  .guest-list { page-break-before: always; }
  .guest-list h2 { font-size: 15px; text-align: center; margin: 0 0 4px; }
  .guest-list .sub { text-align: center; font-size: 10.5px; margin-bottom: 10px; }
  table.persons { width: 100%; border-collapse: collapse; }
  table.persons th, table.persons td { border: 1px solid #000; padding: 4px 8px; height: 30px; }
  table.persons th { background: #eee; }
  table.persons thead { display: table-header-group; }
  table.persons tr { page-break-inside: avoid; }
  table.grid { border-collapse: collapse; }
  table.grid td { border: 1px solid #000; padding: 3px 6px; vertical-align: middle; }
`;

/* Noto Sans Devanagari is loaded explicitly so Hindi prints the same on every
   admin PC, whatever Indic fonts that machine happens to have. */
function page(d, body, { generatedAt, hindi = true } = {}) {
  const title = `Application form ${d.bookingCode || ""}`.trim();
  const bookingCode = d.bookingCode;
  const stamp = fmtDateTime(generatedAt || new Date());
  return `<!doctype html>
<html lang="hi">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet" />
<style>${STYLE}</style>
</head>
<body><div class="page">${bookingCode ? `<div class="ref">Booking ${escapeHtml(bookingCode)}</div>` : ""}${body}</div>
${d.needsGuestList ? guestListPage(d, hindi) : ""}
<div class="footer">Generated by Namaste on ${escapeHtml(stamp)}</div></body>
</html>`;
}

/* The form's three accompanying-person lines, and — when there are more
   people than that — a pointer to the full list on page 2. */
function accompanyingLinesHtml(d, sep = ".") {
  const rows = d.formAccompanying.map((name, i) => `<div>${i + 1}${sep} ${line(name)}</div>`);
  if (d.needsGuestList) {
    const more = d.accompanyingTotal - d.formAccompanying.filter(Boolean).length;
    rows.push(`<div class="more">${more > 0 ? `… and ${more} more — ` : ""}see the list of accompanying persons (page 2)</div>`);
  }
  return rows.join("");
}

/* Page 2, only when more people come than the form has lines for: one
   bordered row per accompanying person, with a blank Remarks column. */
function guestListPage(d, hindi) {
  const stay = [d.arrivalDate, d.departureDate].filter(Boolean).join(" to ");
  const sub = [
    d.bookingCode && `Booking ${d.bookingCode}`,
    d.guestHouseName,
    d.mainGuestName && `Guest: ${d.mainGuestName}`,
    stay,
  ].filter(Boolean);
  return `<div class="guest-list">
  <h2>${hindi ? '<span class="hi">साथ ठहरने वाले व्यक्तियों की सूची</span><br/>' : ""}List of Accompanying Persons</h2>
  <div class="sub">${sub.map(escapeHtml).join(" &nbsp;·&nbsp; ")}</div>
  <table class="persons">
    <thead><tr><th style="width:12%">S.No.</th><th style="width:48%">Name</th><th style="width:40%">Remarks</th></tr></thead>
    <tbody>${d.guestListRows.map((r) => `<tr><td class="center">${r.n}</td><td>${escapeHtml(r.name)}</td><td></td></tr>`).join("")}</tbody>
  </table>
</div>`;
}

/* The SSB form's items 1-6 and its Arrival/Departure table. */
function commonItems(d) {
  return `
  <ol class="items">
    <li class="item"><span>1.</span>
      <span><span class="hi">अतिथि का नाम (स्पष्ट अक्षरों में)</span><br/>Name of the Guest (in Block letters)</span>
      <span class="lines"><div>${line(d.guestName)}</div></span></li>
    <li class="item"><span>2.</span>
      <span><span class="hi">अतिथि का मोबाइल नं.</span>/Mobile No. of the guest</span>
      <span class="lines"><div>${line(d.guestMobile)}</div></span></li>
    <li class="item"><span>3.</span>
      <span><span class="hi">पदनाम सहित पूरा पता</span><br/>Designation with full address</span>
      <span class="lines"><div>${line(d.guestDesignation)}</div><div>${line(d.guestAddress)}</div></span></li>
    <li class="item"><span>4.</span>
      <span><span class="hi">अतिथि के साथ ठहरने वाले व्यक्तियों के नाम तथा अतिथि के साथ उनका संबंध</span><br/>Name(s) of person(s) accompanying the guest and relationship with the guest</span>
      <span class="lines">${accompanyingLinesHtml(d)}</span></li>
    <li class="item"><span>5.</span>
      <span><span class="hi">आगमन का उद्देश्य</span>/Purpose of visit</span>
      <span class="lines"><div>${line(d.purpose)}</div></span></li>
    <li class="item"><span>6.</span>
      <span><span class="hi">ठहरने की अवधि</span>/Duration of the stay</span>
      <span class="lines"><div>${line(d.duration)}</div></span></li>
  </ol>
  <table class="stay">
    <tr><td></td><td class="center"><span class="hi">आगमन</span>/Arrival</td><td class="center"><span class="hi">प्रस्थान</span>/Departure</td></tr>
    <tr><td><span class="hi">दिनांक</span>/Date</td><td class="val">${escapeHtml(d.arrivalDate)}</td><td class="val">${escapeHtml(d.departureDate)}</td></tr>
    <tr><td><span class="hi">समय</span>/Time</td><td class="val">${escapeHtml(d.arrivalTime)}</td><td class="val">${escapeHtml(d.departureTime)}</td></tr>
  </table>`;
}

function officeUse(houseHi, houseEn) {
  return `
  <hr/>
  <div class="center bold"><span class="hi">कार्यालय प्रयोग हेतु</span>/FOR OFFICE USE ONLY</div>
  <p class="small" style="margin:6px 0 2px 40px">
    <span class="hi">दिनांक</span>${line("", "")} <span class="hi">से</span> ${line("")} <span class="hi">तक की अवधि हेतु</span> ${line("")},
  </p>
  <p class="small" style="margin:2px 0">${line("")}<span class="hi">विभाग से अग्रिम भुगतान प्राप्त किया।</span></p>
  <p class="small" style="margin:4px 0 2px 40px">Received Advance &#8377; ${line("")} from ${line("")} deptt.</p>
  <p class="small" style="margin:2px 0">of ${line("")} from ${line("")} to ${line("")}</p>
  <div class="right" style="margin-top:26px">
    <div class="bold"><span class="hi">प्राप्तकर्ता के हस्ताक्षर</span>/Signature of Recipient</div>
    ${houseHi ? `<div class="hi">${houseHi}</div><div>${houseEn}</div>` : ""}
  </div>`;
}

function applicantBlock(d) {
  const phone = `<div class="row"><span><span class="hi">दूरभाष/मोबाइल नं.</span><br/>Ph./Mobile No.</span> ${line(d.applicantMobile)}</div>`;
  return `
  <div class="sig">
    <div class="center bold"><span class="hi">आवेदक का पूर्ण हस्ताक्षर</span><br/>Full signature of the applicant</div>
    <div style="height:18px"></div>
    <div class="row"><span class="hi">नाम</span>/Name ${line(d.applicantName)}</div>
    <div class="row"><span class="hi">पदनाम</span>/Designation ${line(d.applicantDesignation)}</div>
    <div class="row"><span><span class="hi">संकाय/संस्थान/विभाग</span><br/>Faculty/Institute/Department</span> ${line(d.applicantDepartment)}</div>
    <div class="row"><span class="hi">कर्मचारी सं.</span>/Employee No. ${line(d.employeeId)}</div>
    ${phone}
  </div>`;
}

export function ssbFormHtml(d, options = {}) {
  const body = `
  <p class="head1"><span class="hi">शांति स्वरूप भटनागर अतिथि गृह</span> SHANTI SWAROOP BHATNAGAR GUEST HOUSE</p>
  <p class="head1"><span class="hi">काशी हिन्दू विश्वविद्यालय</span> BANARAS HINDU UNIVERSITY</p>
  <div class="topline" style="margin-top:2px">
    <span class="room-no"><span><span class="hi">कक्ष सं.</span>/Room No.</span> ${roomNoBox(d.roomNumbers)}</span>
    <span><span class="hi">दिनांक</span>/Date ${line(d.formDate)}</span>
  </div>
  <div class="title"><span class="hi">शांति स्वरूप भटनागर अतिथि गृह में ठहरने हेतु आवेदन</span>
    <span class="en">APPLICATION FOR THE STAY IN THE SHANTI SWAROOP BHATNAGAR GUEST HOUSE</span></div>
  ${commonItems(d)}
  <ol class="items"><li class="item"><span>7.</span>
    <span><span class="hi">आवेदक के साथ संबंध</span>/Relation of the Applicant</span>
    <span class="lines"><div>${line("")}</div></span></li></ol>
  <div class="cols" style="grid-template-columns:1fr 1fr; margin:8px 0 0 18px">
    <div class="hi small">मैं -<ol style="margin:2px 0; padding-left:18px">
      <li>ठहरने की अवधि के दौरान अतिथि के आचरण,</li>
      <li>निर्धारित तिथि एवं समय पर कमरा(यों) को खाली किए जाने,</li>
      <li>अतिथि के किसी बकाया का भुगतान जमा करने हेतु</li></ol>उत्तरदायी हूँ।</div>
    <div class="small">I am responsible for :<ol style="margin:2px 0; padding-left:18px">
      <li>The conduct of the guest during stay.</li>
      <li>To vacate the room(s) on the stipulated date and time.</li>
      <li>To deposit any arrear of payment of guest.</li></ol></div>
  </div>
  <div class="cols" style="grid-template-columns:1fr 1fr; margin-top:22px">
    <div class="center small" style="padding-top:4px">
      <div class="hi">अग्रसारित/Forwarded by</div>
      <div style="height:48px"></div>
      <div class="hi">कुलपति/संकाय प्रमुख/निदेशक/विभागाध्यक्ष/समन्वयक/प्रशा. संरक्षक</div>
      <div>V.C./Dean/Director/Head/Coordinator/Admin Warden</div>
      <div><span class="hi">मुहर</span>/SEAL</div>
    </div>
    ${applicantBlock(d)}
  </div>
  <div class="bold" style="margin-top:8px"><span class="hi">नोट</span>/N.B.</div>
  <ol class="notes">
    <li><span class="hi">अपूर्ण प्रपत्र स्वीकार नहीं किया जाएगा।</span><br/>Incomplete form will not be entertained.</li>
    <li><span class="hi">अनुरोध केवल (विभागीय) परिचर बही के माध्यम से भेजा जाए।</span><br/>Request(s) should be sent through (Departmental) peon book only.</li>
    <li><span class="hi">कुल अग्रिम धनराशि प्राप्त होने पर ही कमरा आरक्षित किया जाएगा।</span><br/>Reservation will be made only on receipt of FULL ADVANCE.</li>
    <li><span class="hi">भुगतान रसीद कमरा छोड़ने के उपरांत दी जाएगी।</span><br/>Payment receipt will be generated after check out of room.</li>
    <li><span class="hi">निरस्तीकरण करवाए जाने पर, एक दिन के आवंटन शुल्क की कटौती की जाएगी और शेष राशि वापस कर दी जाएगी।</span><br/>In case of cancellation, one-day allotment charge be deducted and the rest will be refunded.</li>
    <li><span class="hi">चेक इन से चेक आउट की अवधि 24 घंटे से कम होने पर कमरों का आवंटन पूरे एक दिन के लिए माना जाएगा।</span><br/>Check in to check out time less than 24 hrs. is counted as one day for allotment of rooms.</li>
  </ol>
  ${officeUse("शांति स्वरूप भटनागर अतिथि गृह", "Shanti Swaroop Bhatnagar Guest House")}`;
  return page(d, body, options);
}

/* Item 8's table on the Faculty form has three printed rows. Requested room
   types land on the row they fit — two beds, three beds, dormitory — and any
   other type (Suite, a hall, the lawn) gets a row of its own beneath. */
const FACULTY_ROOM_ROWS = ["2 Bed", "3 Bed", "Dormitory"];

function facultyRoomRow(r) {
  const name = r.name.toLowerCase();
  if (name.includes("dorm")) return "Dormitory";
  if (/\bsuite\b|lawn|hall|gen\b/.test(name)) return null;
  if (r.capacity === 3 || /3 bed|twin bed \+ single/.test(name)) return "3 Bed";
  if (r.capacity === 2 || /2 bed|two bed/.test(name)) return "2 Bed";
  return null;
}

/* Item 9, answered from what was asked for: a lawn or hall type whose name
   mentions the generator is (a), one without is (b). */
function facultyVenueAnswers(d) {
  const venues = d.roomRequests.filter((r) => /lawn|hall|dining/i.test(r.name));
  const withGen = venues.some((r) => /gen/i.test(r.name));
  const withoutGen = venues.some((r) => !/gen/i.test(r.name));
  return {
    withGen: venues.length ? (withGen ? "Yes" : "No") : "No",
    withoutGen: venues.length ? (withoutGen ? "Yes" : "No") : "No",
    purpose: venues.length ? d.purpose : "",
  };
}

export function facultyFormHtml(d, options = {}) {
  const counts = new Map(FACULTY_ROOM_ROWS.map((row) => [row, 0]));
  const extra = [];
  for (const r of d.roomRequests) {
    const row = facultyRoomRow(r);
    if (row) counts.set(row, counts.get(row) + r.count);
    else extra.push(r);
  }
  const roomRows = [
    ...FACULTY_ROOM_ROWS.map((row) => [row, counts.get(row) || ""]),
    ...extra.map((r) => [r.name, r.count]),
  ];
  const venue = facultyVenueAnswers(d);
  const en = `font-family:"Times New Roman", Times, serif`;
  const body = `
  <div style="display:flex; justify-content:space-between; align-items:flex-end">
    <div style="flex:1; text-align:center">
      <div class="bold" style="font-family:Arial, Helvetica, sans-serif; font-size:19px">FACULTY GUEST HOUSE</div>
      <div class="bold" style="${en}; font-size:20px">BANARAS HINDU UNIVERSITY</div>
    </div>
  </div>
  <div class="topline" style="margin-top:0">
    <span class="room-no"><span>Room No.</span> ${roomNoBox(d.roomNumbers)}</span>
    <span><i>Dated</i> ${line(d.formDate)}</span>
  </div>
  <div class="center bold" style="font-size:13px; text-decoration:underline; margin:5px 0 6px">APPLICATION FOR THE STAY IN THE FACULTY GUEST HOUSE</div>
  <div class="center" style="margin-bottom:6px">
    <span class="bold">Booking Type :</span>
    &nbsp; ${box(d.isOfficial)} Official &nbsp;&nbsp; ${box(d.isNonOfficial)} Non-Official
  </div>
  <ol class="items">
    <li class="item"><span>1.</span><span>Name of the guest (In Block Letters)</span>
      <span class="lines"><div>${line(d.guestName)}</div></span></li>
    <li class="item"><span>2.</span><span>Mobile No. of the guest</span>
      <span class="lines"><div>${line(d.guestMobile)}</div></span></li>
    <li class="item"><span>3.</span><span>Designation with full address</span>
      <span class="lines"><div>${line(d.guestDesignation)}</div><div>${line(d.guestAddress)}</div></span></li>
    <li class="item"><span>4.</span><span>Number(s) of person(s) accompanying the guest and relationship with guest.${
      d.accompanyingCount ? ` <b>(${d.accompanyingCount})</b>` : ""}</span>
      <span class="lines">${accompanyingLinesHtml(d, "")}</span></li>
    <li class="item"><span>5.</span><span>Purpose of visit</span>
      <span class="lines"><div>${line(d.purpose)}</div></span></li>
    <li class="item" style="align-items:start"><span>6.</span><span>Duration of stay${d.duration ? ` <b>(${escapeHtml(d.duration)})</b>` : ""}</span>
      <span><table class="stay" style="width:100%; margin:0">
        <tr><td></td><td class="center">Arrival</td><td class="center">Departure</td></tr>
        <tr><td class="center">Date</td><td class="val">${escapeHtml(d.arrivalDate)}</td><td class="val">${escapeHtml(d.departureDate)}</td></tr>
        <tr><td class="center">Time</td><td class="val">${escapeHtml(d.arrivalTime)}</td><td class="val">${escapeHtml(d.departureTime)}</td></tr>
      </table></span></li>
    <li class="item" style="grid-template-columns:18px 1fr"><span>7.</span>
      <span style="display:flex; gap:4px; align-items:flex-end">Relation of the Applicant ${line("")}</span></li>
    <li class="item" style="align-items:start"><span>8.</span><span>Number of room required :</span>
      <span><table class="stay" style="width:100%; margin:0">
        <tr><td class="center">Type of Room</td><td class="center">Required (Number)</td></tr>
        ${roomRows.map(([name, n]) => `<tr><td>${escapeHtml(name)}</td><td class="val">${escapeHtml(n)}</td></tr>`).join("")}
      </table></span></li>
  </ol>
  <div style="display:grid; grid-template-columns:18px 1fr; column-gap:6px; margin-top:6px">
    <span>9</span>
    <div>
      <div style="display:flex; gap:4px; align-items:flex-end">(a) Do you want Dining Hall/Lawn (with Generator)? &nbsp;(Yes/No) ${line(venue.withGen, "short")}</div>
      <div style="display:flex; gap:4px; align-items:flex-end">(b) Do you want Dining Hall/Lawn (without Generator)? (Yes/No) ${line(venue.withoutGen, "short")}</div>
      <div style="display:flex; gap:4px; align-items:flex-end">(c) Purpose of Dining Hall/Lawn booking (Birthday/Engagement/Official meeting/Others specify) ${line(venue.purpose)}</div>
    </div>
  </div>
  <div class="cols" style="grid-template-columns:1fr 1fr; margin-top:8px">
    <div>
      <div>I am responsible for :</div>
      <ol style="margin:2px 0; padding-left:18px">
        <li>The conduct of the guest during stay.</li>
        <li>To vacate the room(s) on the stipulated date and time.</li>
        <li>To deposit any arrear of payment of guest.</li>
      </ol>
      <div class="center" style="margin-top:22px">Forwarded by</div>
      <div class="center" style="margin-top:28px">V.C./Dean/Director/Head/Co-ordinator/Warden</div>
      <div class="center">(Seal)</div>
    </div>
    <div class="sig" style="padding-top:22px">
      <div class="center bold">Full signature of the applicant with<br/>Name and Designation</div>
      <div class="row">Name ${line(d.applicantName)}</div>
      <div class="row">Designation ${line(d.applicantDesignation)}</div>
      <div class="row">Faculty/Institute/Department ${line(d.applicantDepartment)}</div>
      <div class="row">Employee No. ${line(d.employeeId)}</div>
      <div class="row">Tel. No./Mob. No. ${line(d.applicantMobile)}</div>
    </div>
  </div>
  <div style="display:grid; grid-template-columns:40px 1fr; margin-top:10px; font-size:10.5px">
    <span class="bold">N.B. :</span>
    <ol style="margin:0; padding-left:18px">
      <li>Incomplete form will not be entertained.</li>
      <li>Request(s) should be sent through (Departmental) peon book only.</li>
      <li>Reservation will be made only on receipt of FULL ADVANCE.</li>
      <li>Payment receipt will be generated after departure of guest.</li>
      <li>In case of cancellation, one-day allotment charge be deducted and the rest will be refunded.</li>
      <li>Allotment may be cancelled subject to official purpose.</li>
      <li>Check in to check out time less than 24 hrs. is counted as one day for rent of the rooms.</li>
      <li>No outside Generator is allowed during booking of Lawn/Dining Hall except the exigency. The facility of guest house is available on charge basis.</li>
      <li>Musical instruments will not be permitted during booking of Lawn/Dining Hall.</li>
    </ol>
  </div>
  <div style="border-top:1px dotted #000; margin:10px 0 6px"></div>
  <div class="center bold">FOR OFFICE USE ONLY</div>
  <div style="display:flex; gap:4px; align-items:flex-end; margin:8px 0 0 40px">Received Advance Rs. ${line("")} from ${line("")}</div>
  <div style="display:flex; gap:4px; align-items:flex-end; margin-top:6px">${line("")} Deptt. of ${line("")} from ${line("")}</div>
  <div style="display:flex; justify-content:space-between; align-items:flex-end; margin-top:6px">
    <div style="display:flex; gap:4px; align-items:flex-end; width:50%">To ${line("")}</div>
    <div class="right" style="margin-top:14px"><div class="bold">Signature of Recipient</div><div class="bold">Phone : 0542-2366782</div></div>
  </div>`;
  return page(d, body, { ...options, hindi: false });
}

export function ldUghFormHtml(d, options = {}) {
  const { sealUrl = "" } = options;
  const guestDetails = [d.guestDesignation, d.guestAddress, d.guestMobile].filter(Boolean).join(", ");
  const body = `
  <div style="display:flex; align-items:center; gap:14px">
    ${sealUrl ? `<img src="${escapeHtml(sealUrl)}" alt="" style="height:78px" />` : ""}
    <div style="flex:1">
      <div style="display:flex; justify-content:space-between; align-items:baseline">
        <div class="center"><div class="hi bold" style="font-size:19px">काशी हिन्दू विश्वविद्यालय</div><div class="hi bold">विश्व विद्यालय अतिथि गृह संकुल</div></div>
        <div class="center"><div class="bold" style="font-size:17px; font-family:Arial, sans-serif">BANARAS HINDU UNIVERSITY</div><div class="bold" style="font-family:Arial, sans-serif">UNIVERSITY GUEST HOUSE COMPLEX</div></div>
      </div>
      <div style="display:flex; justify-content:space-between; font-size:10.5px; margin-top:3px">
        <span><span class="hi bold">ई-मेल पता</span>/E-Mail: ghc@bhu.ac.in</span>
        <span><span class="hi bold">दूरभाष सं.</span>/Phone No. 0542-2366691</span>
      </div>
    </div>
  </div>
  <div class="title" style="margin:6px 0"><span class="hi">लक्ष्मण दास अतिथि गृह/विश्व विद्यालय अतिथि गृह में ठहरने हेतु आवेदन</span>
    <span class="en" style="font-family:Arial, sans-serif">APPLICATION FOR THE STAY IN THE LAKSHMAN DAS/UNIVERSITY GUEST HOUSE</span></div>
  <div style="display:flex; gap:10px; align-items:stretch; font-size:10.5px">
    <table class="grid">
      <tr><td class="bold"><span class="hi">आधिकारिक अतिथि</span><br/>Official Guest</td><td class="center">${box(d.isOfficial)}</td></tr>
      <tr><td class="bold"><span class="hi">गैर-आधिकारिक अतिथि</span><br/>Non-Official Guest</td><td class="center">${box(d.isNonOfficial)}</td></tr>
    </table>
    <table class="grid center">
      <tr><td class="bold"><span class="hi">एल.डी.</span><br/>L.D.</td><td class="bold"><span class="hi">एल.डी (विस्तार)</span><br/>L.D. Ext.</td><td class="bold"><span class="hi">वि.अ.गृ.</span><br/>U.G.H</td></tr>
      <tr><td>${box(d.ldUghTick === "ld")}</td><td>${box(d.ldUghTick === "ldExt")}</td><td>${box(d.ldUghTick === "ugh")}</td></tr>
    </table>
    <table class="grid" style="flex:1"><tr><td class="bold"><span class="hi">दिनांक</span>/Date: ${d.formDate ? line(d.formDate) : "______/________/20________"}</td></tr></table>
  </div>
  <ol class="items" style="margin-top:8px">
    <li class="item"><span>1.</span>
      <span><span class="hi">अतिथि का नाम (स्पष्ट अक्षरों में) :</span><br/>Name of the Guest (In Block letters)</span>
      <span class="lines"><div>${line(d.guestName)}</div></span></li>
    <li class="item"><span>2.</span>
      <span><span class="hi">अतिथि का पदनाम, पूरा पता एवं मोबाइल नम्बर</span><br/>Designation, full address &amp; Mobile Number</span>
      <span class="lines"><div>${line(guestDetails)}</div></span></li>
    <li class="item"><span>3.</span>
      <span><span class="hi">अतिथि के साथ ठहरने वाले व्यक्तियों के नाम तथा अतिथि के साथ उनका संबंध</span><br/>Name(s) of person(s) accompanying the Guest and relationship with the Guest</span>
      <span class="lines">${accompanyingLinesHtml(d)}</span></li>
    <li class="item"><span>4.</span>
      <span><span class="hi">कक्ष/कक्षों की संख्या</span>/Number of Room(s)</span>
      <span class="lines"><div>${line(d.rooms)}</div></span></li>
    <li class="item"><span>5.</span>
      <span><span class="hi">आगमन का उद्देश्य</span>/Purpose of visit<br/>(Attach proof in case of Official)</span>
      <span class="lines"><div>${line(d.purpose)}</div></span></li>
    <li class="item"><span>6.</span>
      <span><span class="hi">ठहरने का विवरण</span><br/>Details of Stay</span>
      <span>
        <table class="stay" style="width:100%; margin:0">
          <tr><td></td><td class="center"><span class="hi">आगमन</span>/Arrival</td><td class="center"><span class="hi">प्रस्थान</span>/Departure</td><td class="center"><span class="hi">दिवस/दिवसों की सं.</span><br/>No. of Days</td></tr>
          <tr><td><span class="hi">दिनांक</span>/Date</td><td class="val">${escapeHtml(d.arrivalDate)}</td><td class="val">${escapeHtml(d.departureDate)}</td><td class="val" rowspan="2">${escapeHtml(d.days)}</td></tr>
          <tr><td><span class="hi">समय</span>/Time</td><td class="val">${escapeHtml(d.arrivalTime)}</td><td class="val">${escapeHtml(d.departureTime)}</td></tr>
        </table>
      </span></li>
  </ol>
  <div class="hi bold" style="margin-top:6px">नोट:-</div>
  <div class="hi small">
    (1) अतिथि गृह में प्रवास के दौरान समस्त नियमों का पालन करना होगा एवं अतिथि का आचरण, अतिथि गृह नियमावली के अनुरूप होगा। किसी भी कदाचरण की स्थिति में तत्काल कक्ष खाली करना होगा और इसकी सूचना विश्वविद्यालय प्रशासन को दी जाएगी।<br/>
    (2) गैर-आधिकारिक अतिथि के ठहरने/खाने-पीने का भुगतान स्वयं उनके द्वारा अथवा आवेदक के द्वारा किया जाएगा।<br/>
    (3) निर्धारित तिथि एवं समय पर कक्ष/कक्षों को खाली किया जाना सुनिश्चित करना होगा।
  </div>
  <div class="cols" style="grid-template-columns:1fr 1.15fr; margin-top:22px">
    <div class="center bold small" style="padding-top:24px">
      <div class="hi">विभागाध्यक्ष/प्राचार्य/संकायप्रमुख/निदेशक द्वारा अग्रसारित</div>
      <div>Forwarded by Head/Principal/Dean/Director</div>
      <div><span class="hi">मुहर</span>/Seal</div>
    </div>
    <div class="sig small">
      <div class="center bold"><span class="hi">आवेदक के पूर्ण हस्ताक्षर</span><br/>Full Signature of the Applicant</div>
      <div class="row"><span><span class="hi">नाम एवं कर्मचारी सं.</span><br/>Name &amp; E.ID. No.</span> ${line([d.applicantName, d.employeeId].filter(Boolean).join(" / "))}</div>
      <div class="row"><span><span class="hi">पदनाम एवं विभाग</span><br/>Designation &amp; Department</span> ${line([d.applicantDesignation, d.applicantDepartment].filter(Boolean).join(", "))}</div>
      <div class="row"><span class="hi">मोबाइल सं.</span>/Mobile No. ${line(d.applicantMobile)}</div>
      <div class="row"><span><span class="hi">ई-मेल आईडी</span><br/>E-Mail ID</span> ${line(d.applicantEmail)}</div>
    </div>
  </div>
  <table class="grid" style="width:100%; margin-top:14px; font-size:10.5px">
    <tr><td colspan="4" class="center bold"><span class="hi">भुगतान का प्रकार</span>/Mode of Payment</td></tr>
    <tr>
      <td><span class="hi">अतिथि द्वारा</span><br/>By Guest &nbsp;${box(d.paymentTick === "byGuest")}</td>
      <td><span class="hi">आवेदक द्वारा</span><br/>By Applicant &nbsp;${box(d.paymentTick === "byApplicant")}</td>
      <td><span class="hi">विश्वविद्यालय खाता</span><br/>University A/C &nbsp;${box(d.paymentTick === "university")}</td>
      <td><span class="hi">विशेष निधि/परियोजना खाता</span><br/>Special Fund/Project A/C &nbsp;${box(false)}</td>
    </tr>
    ${d.paymentOther ? `<tr><td colspan="4" class="small">Other: <b>${escapeHtml(d.paymentOther)}</b></td></tr>` : ""}
  </table>
  <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-top:18px">
    <table class="grid" style="width:44%; font-size:10.5px">
      <tr><td colspan="2" class="center bold"><span class="hi">कार्यालय प्रयोग हेतु</span>/FOR OFFICE USE</td></tr>
      <tr><td style="width:40%"><span class="hi">द्वारा प्राप्त</span><br/>Received by</td><td></td></tr>
      <tr><td><span class="hi">दिनांक</span>/Date</td><td></td></tr>
      <tr><td><span class="hi">क्रम सं.</span>/S.N.</td><td></td></tr>
    </table>
    <div class="center bold small" style="padding-top:8px">
      <div class="hi">भुगतान हेतु अधिकृत व्यक्ति के हस्ताक्षर</div>
      <div>Signature of Authorised Person for Payment</div>
      <div><span class="hi">मुहर</span>/Seal</div>
    </div>
  </div>`;
  return page(d, body, options);
}

/* options: { sealUrl, generatedAt } — generatedAt stamps the footer and
   defaults to now. */
export function applicationFormHtml(d, options = {}) {
  if (d.kind === "ssb") return ssbFormHtml(d, options);
  if (d.kind === "faculty") return facultyFormHtml(d, options);
  return ldUghFormHtml(d, options);
}
